<template>
    <section class="min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full container mx-auto">
            <div class="flex flex-col h-full w-full p-8">

                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <router-link to="/training-module"
                        class="hover:text-blue-700 transition font-medium">Internship</router-link>
                    <span class="mx-1">/</span>
                    <span class="text-blue-900 font-semibold">Internship Report</span>
                </nav>

                <!-- Page Header -->
                <div class="bg-white border border-gray-200 rounded-3xl shadow-md p-8 mb-8">
                    <h1 class="text-3xl font-bold text-blue-900 flex items-center gap-3">
                        <span class="text-4xl">📊</span>
                        Internship Report
                        <div v-if="isLoading" class="ml-2">
                            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                    </h1>
                    <p class="text-gray-600 mt-2 text-base leading-relaxed max-w-3xl">
                        Internship reporting dashboard. View program statistics and placement insights across academic
                        years.
                    </p>
                </div>

                <div v-if="hideForNow" class="px-4 sm:px-8 flex flex-row flex-wrap w-full pb-4 sm:pb-8">
                    <div v-if="role == 'admin' || role == 'superadmin' || role == 'Viewer'"
                        class="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8 mt-4 sm:mt-8">
                        <tile v-for="(item, index) in adminMenu" :key="index" :item="item"></tile>
                    </div>
                    <div v-else-if="role == 'partner'"
                        class="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8 mt-4 sm:mt-8">
                        <tile v-for="(item, index) in partnerMenu" :key="index" :item="item"></tile>
                    </div>
                    <div v-else-if="isActiveStudent"
                        class="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8 mt-4 sm:mt-8">
                        <tile v-for="(item, index) in activeStudentMenu" :key="index" :item="item"></tile>
                    </div>
                </div>

                <!-- Loading State -->
                <transition name="fade" mode="out-in">
                    <div v-if="isLoading" key="loading" class="space-y-8">
                        <!-- Loading Header -->
                        <div class="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8">
                            <div class="bg-gradient-to-r from-gray-200 to-gray-300 px-6 py-4 shimmer">
                                <div class="h-8 bg-gray-400 rounded w-1/3 mb-2 shimmer-bar"></div>
                                <div class="h-4 bg-gray-400 rounded w-1/2 shimmer-bar"></div>
                            </div>
                            <div class="p-6">
                                <!-- Summary Cards Loading -->
                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                                    <div v-for="i in 4" :key="i" class="bg-gray-100 rounded-lg p-4 shimmer">
                                        <div class="flex items-center justify-between">
                                            <div class="flex-1">
                                                <div class="h-4 bg-gray-300 rounded w-20 mb-2 shimmer-bar"></div>
                                                <div class="h-8 bg-gray-300 rounded w-16 shimmer-bar"></div>
                                            </div>
                                            <div class="w-12 h-12 bg-gray-300 rounded-full shimmer-bar"></div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Charts Loading -->
                                <div class="mt-8 space-y-8">
                                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        <div v-for="i in 2" :key="i" class="bg-gray-50 rounded-xl p-6 shimmer">
                                            <div class="h-6 bg-gray-300 rounded w-3/4 mb-4 shimmer-bar"></div>
                                            <div class="h-80 bg-gray-200 rounded shimmer-bar"></div>
                                        </div>
                                    </div>
                                    <div class="bg-gray-50 rounded-xl p-6 shimmer">
                                        <div class="h-6 bg-gray-300 rounded w-1/2 mb-4 shimmer-bar"></div>
                                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            <div class="h-80 bg-gray-200 rounded shimmer-bar"></div>
                                            <div class="h-80 bg-gray-200 rounded shimmer-bar"></div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Table Loading -->
                                <div class="mt-8 bg-gray-50 rounded-lg overflow-hidden shimmer">
                                    <div class="h-12 bg-gray-200 shimmer-bar"></div>
                                    <div v-for="i in 6" :key="i"
                                        class="h-12 bg-gray-100 border-t border-gray-200 shimmer-bar"></div>
                                </div>

                                <!-- Insights Loading -->
                                <div class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div v-for="i in 2" :key="i" class="bg-gray-100 rounded-lg p-4 shimmer">
                                        <div class="h-5 bg-gray-300 rounded w-1/2 mb-2 shimmer-bar"></div>
                                        <div class="h-4 bg-gray-300 rounded w-3/4 shimmer-bar"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>

                <!-- Internship Program Statistics Section - Dynamic Data -->
                <transition name="fade" mode="out-in">
                    <div v-if="!isLoading && academicYears.length > 0" key="content">
                        <div v-for="(item, index) in academicYears" :key="index"
                            class="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8">
                            <!-- Header -->
                            <div class="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
                                <h2 class="text-xl sm:text-2xl font-bold text-white">
                                    ADPOLY Internship Program Academic Year {{ item.academic_year }}
                                </h2>
                                <p class="text-primary-100 text-sm sm:text-base mt-1">
                                    Department-wise internship distribution across academic periods
                                </p>
                            </div>

                            <!-- Statistics Cards -->
                            <div class="p-6">
                                <!-- Summary Cards -->
                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                                    <div
                                        class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                                        <div class="flex items-center justify-between">
                                            <div>
                                                <p class="text-blue-600 text-sm font-medium">Summer 1</p>
                                                <p class="text-2xl font-bold text-blue-800">{{ item.summary.summer1 }}
                                                </p>
                                            </div>
                                            <div
                                                class="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                                                <font-awesome-icon icon="sun" class="text-blue-600 text-xl" />
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        class="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                                        <div class="flex items-center justify-between">
                                            <div>
                                                <p class="text-green-600 text-sm font-medium">Summer 2</p>
                                                <p class="text-2xl font-bold text-green-800">{{ item.summary.summer2 }}
                                                </p>
                                            </div>
                                            <div
                                                class="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                                                <font-awesome-icon icon="sun" class="text-green-600 text-xl" />
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                                        <div class="flex items-center justify-between">
                                            <div>
                                                <p class="text-purple-600 text-sm font-medium">Spring</p>
                                                <p class="text-2xl font-bold text-purple-800">{{ item.summary.spring }}
                                                </p>
                                            </div>
                                            <div
                                                class="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                                                <font-awesome-icon icon="leaf" class="text-purple-600 text-xl" />
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        class="bg-gradient-to-br from-lime-50 to-lime-100 rounded-lg p-4 border border-lime-200">
                                        <div class="flex items-center justify-between">
                                            <div>
                                                <p class="text-lime-600 text-sm font-medium">Total</p>
                                                <p class="text-2xl font-bold text-lime-800">{{ item.summary.total }}
                                                </p>
                                            </div>
                                            <div
                                                class="w-12 h-12 bg-lime-200 rounded-full flex items-center justify-center">
                                                <font-awesome-icon icon="users" class="text-lime-600 text-xl" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Charts Section -->
                                <div class="mt-8 space-y-8">
                                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        <!-- Period Distribution Chart -->
                                        <div class="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                                            <h3 class="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                                <font-awesome-icon icon="chart-bar" class="text-blue-600 mr-2" />
                                                Internship Distribution by Academic Period
                                            </h3>
                                            <div class="h-80">
                                                <apexchart type="bar" height="100%" :options="periodChartOptions"
                                                    :series="getPeriodChartSeries(item.academic_year)">
                                                </apexchart>
                                            </div>
                                        </div>
                                        <!-- Detailed Breakdown Chart -->
                                        <div class="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                                            <h3 class="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                                <font-awesome-icon icon="chart-line" class="text-purple-600 mr-2" />
                                                Department Breakdown by Academic Period
                                            </h3>
                                            <div class="h-80">
                                                <apexchart type="bar" height="100%" :options="stackedChartOptions"
                                                    :series="getStackedChartSeries(item.academic_year)">
                                                </apexchart>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Department Performance Chart -->
                                    <div class="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                                        <h3 class="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                            <font-awesome-icon icon="chart-pie" class="text-green-600 mr-2" />
                                            Department Performance Overview
                                        </h3>
                                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            <div class="h-80">
                                                <apexchart type="bar" height="100%" :options="departmentChartOptions"
                                                    :series="getDepartmentChartSeries(item.academic_year)">
                                                </apexchart>
                                            </div>
                                            <div class="h-80">
                                                <apexchart type="donut" height="100%"
                                                    :options="getPieChartOptions(item.academic_year)"
                                                    :series="getPieChartSeries(item.academic_year)">
                                                </apexchart>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Placement Type Breakdown -->
                                <div v-if="getPlacementBreakdown(item.academic_year) && getPlacementBreakdown(item.academic_year).length > 0"
                                    class="mt-8 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                                    <h3 class="text-lg font-bold text-gray-800 mb-6 flex items-center">
                                        <font-awesome-icon icon="chart-pie" class="text-orange-600 mr-2" />
                                        Placement Type Distribution
                                    </h3>
                                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        <div class="h-80">
                                            <apexchart type="donut" height="100%"
                                                :options="getPlacementTypeChartOptions(item.academic_year)"
                                                :series="getPlacementTypeChartSeries(item.academic_year)">
                                            </apexchart>
                                        </div>
                                        <div class="space-y-4">
                                            <div v-for="placement in getPlacementBreakdown(item.academic_year)"
                                                :key="placement.placement_type"
                                                class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                                <div class="flex items-center">
                                                    <div class="w-4 h-4 rounded-full mr-3"
                                                        :style="{ backgroundColor: getPlacementColor(placement.placement_type, item.academic_year) }">
                                                    </div>
                                                    <span class="font-medium text-gray-700">{{ placement.placement_type
                                                        }}</span>
                                                </div>
                                                <div class="text-right">
                                                    <div class="text-lg font-bold text-gray-900">{{ placement.count }}
                                                    </div>
                                                    <div class="text-sm text-gray-500">{{ placement.percentage }}%</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Department Breakdown Table -->
                                <div class="mt-8 bg-gray-50 rounded-lg overflow-hidden">
                                    <div class="overflow-x-auto">
                                        <table class="w-full">
                                            <thead class="bg-gray-100">
                                                <tr>
                                                    <th
                                                        class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">
                                                        Department
                                                    </th>
                                                    <th
                                                        class="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-gray-200">
                                                        Summer 1
                                                    </th>
                                                    <th
                                                        class="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-gray-200">
                                                        Summer 2
                                                    </th>
                                                    <th
                                                        class="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-gray-200">
                                                        Spring
                                                    </th>
                                                    <th
                                                        class="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-gray-200">
                                                        Total
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody class="divide-y divide-gray-200">
                                                <tr v-for="dept in getAcademicYearDepartments(item.academic_year)"
                                                    :key="dept.name" class="hover:bg-gray-50 transition-colors">
                                                    <td class="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">
                                                        {{ dept.name }}
                                                    </td>
                                                    <td class="px-4 py-3 text-center text-sm text-gray-700">{{
                                                        dept.summer1 }}
                                                    </td>
                                                    <td class="px-4 py-3 text-center text-sm text-gray-700">{{
                                                        dept.summer2 }}
                                                    </td>
                                                    <td class="px-4 py-3 text-center text-sm text-gray-700">{{
                                                        dept.spring }}
                                                    </td>
                                                    <td
                                                        class="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                                        {{ dept.total }}</td>
                                                </tr>
                                            </tbody>
                                            <tfoot class="bg-yellow-50 border-t-2 border-yellow-200">
                                                <tr>
                                                    <td class="px-4 py-3 text-sm font-bold text-gray-900">
                                                        Total
                                                    </td>
                                                    <td class="px-4 py-3 text-center text-sm font-bold text-gray-900">{{
                                                        getAcademicYearSummary(item.academic_year, 'summer1') }}
                                                    </td>
                                                    <td class="px-4 py-3 text-center text-sm font-bold text-gray-900">{{
                                                        getAcademicYearSummary(item.academic_year, 'summer2') }}
                                                    </td>
                                                    <td class="px-4 py-3 text-center text-sm font-bold text-gray-900">{{
                                                        getAcademicYearSummary(item.academic_year, 'spring') }}
                                                    </td>
                                                    <td class="px-4 py-3 text-center text-sm font-bold text-gray-700">
                                                        {{ getAcademicYearSummary(item.academic_year, 'total') }}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>

                                <!-- Additional Insights -->
                                <div class="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                        <h4 class="text-sm font-semibold text-blue-800 mb-2">Top Performing Department
                                        </h4>
                                        <p class="text-blue-700 text-sm">{{
                                            getTopPerformingDepartment(item.academic_year) }}</p>
                                    </div>
                                    <div class="bg-green-50 rounded-lg p-4 border border-green-200">
                                        <h4 class="text-sm font-semibold text-green-800 mb-2">Peak Period</h4>
                                        <p class="text-green-700 text-sm">{{ getPeakPeriod(item.academic_year) }}</p>
                                    </div>
                                    <div class="bg-orange-50 rounded-lg p-4 border border-orange-200">
                                        <h4 class="text-sm font-semibold text-orange-800 mb-2">Most Popular Placement
                                        </h4>
                                        <p class="text-orange-700 text-sm">{{
                                            getMostPopularPlacement(item.academic_year) }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>

                <!-- No Data State -->
                <transition name="fade" mode="out-in">
                    <div v-if="!isLoading && academicYears.length === 0" key="no-data"
                        class="bg-white rounded-xl shadow-lg border border-gray-100 p-8 text-center">
                        <div class="text-gray-500">
                            <font-awesome-icon icon="chart-line" class="text-6xl mb-4" />
                            <h3 class="text-xl font-semibold mb-2">No Internship Data Available</h3>
                            <p class="text-gray-600">There are currently no internship statistics to display.</p>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </section>
</template>

<script>
import Tile from "../../components/shared/Tile.vue";
import VueApexCharts from "vue3-apexcharts";
import gql from "graphql-tag";

export default {
    name: "internship-report",
    components: {
        Tile,
        apexchart: VueApexCharts,
    },
    data() {
        return {
            hideForNow: false,
            // Loading state
            isLoading: true,
            // Dynamic academic years data - will be populated from GraphQL
            academicYears: [],
            adminMenu: [
                {
                    id: 1,
                    isActive: true,
                    isImage: true,
                    path: "/training",
                    title: "Internship Program",
                    icon: ``,
                    image: require('../../assets/images/icons/img3.png'),
                    isNotificationNumber: 0,
                    popOverContent: `<ul class="list-disc pl-4">
                                    <li>View Internship Programs</li>
                                  </ul>`,
                    showPopOver: true,
                },
                {
                    id: 2,
                    isActive: true,
                    isImage: true,
                    path: "/post-training",
                    title: "Post Internship",
                    icon: ``,
                    image: require('../../assets/images/icons/img2.png'),
                    isNotificationNumber: 0,
                    popOverContent: `<ul class="list-disc pl-4">
                                    <li>View Internship</li>
                                    <li>Post Internship</li>
                                  </ul>`,
                    showPopOver: true,
                },
            ],
            partnerMenu: [
                {
                    id: 1,
                    isActive: true,
                    isImage: true,
                    path: "/training-approval",
                    title: "Internship Program",
                    icon: ``,
                    image: require('../../assets/images/icons/img3.png'),
                    isNotificationNumber: 0,
                    popOverContent: `<ul class="list-disc pl-4">
                                    <li>View Internship</li>
                                    <li>Approve / Reject Internship</li>
                                  </ul>`,
                    showPopOver: true,
                },
                {
                    id: 2,
                    isActive: true,
                    isImage: true,
                    path: "/post-training",
                    title: "Post Internship",
                    icon: ``,
                    image: require('../../assets/images/icons/img2.png'),
                    isNotificationNumber: 0,
                    popOverContent: `<ul class="list-disc pl-4">
                                    <li>View Internship</li>
                                    <li>Post Internship</li>
                                  </ul>`,
                    showPopOver: true,
                },
            ],
            activeStudentMenu: [
                {
                    id: 1,
                    isActive: true,
                    isImage: true,
                    path: "/training-invitation",
                    title: "Internship Program",
                    icon: ``,
                    image: require('../../assets/images/icons/img3.png'),
                    isNotificationNumber: 0,
                    popOverContent: ``,
                    showPopOver: false,
                },
                {
                    id: 2,
                    isActive: true,
                    isImage: true,
                    path: "/post-training-opportunities",
                    title: "Post Internship",
                    icon: ``,
                    image: require('../../assets/images/icons/img2.png'),
                    isNotificationNumber: 0,
                    popOverContent: `<ul class="list-disc pl-4">
                                    <li>View List of Post Internships</li>
                                    <li>Apply for Post Internships</li>
                                  </ul>`,
                    showPopOver: true,
                },
            ],
            // Chart Data
            periodChartOptions: {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    }
                },
                colors: ['#3B82F6', '#10B981', '#8B5CF6'],
                plotOptions: {
                    bar: {
                        columnWidth: '60%',
                        distributed: true,
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }
                },
                xaxis: {
                    categories: ['Summer 1', 'Summer 2', 'Spring'],
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Internship'
                    }
                },
                grid: {
                    borderColor: '#f1f5f9'
                }
            },
            departmentChartOptions: {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    }
                },
                colors: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4'],
                plotOptions: {
                    bar: {
                        columnWidth: '70%',
                        horizontal: true,
                        distributed: true,
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '11px',
                        fontWeight: 'bold'
                    }
                },
                xaxis: {
                    categories: ['Electromechanical', 'Information Security', 'Petroleum', 'Aviation (AET/AMT)', 'Meteorology'],
                    labels: {
                        style: {
                            fontSize: '11px'
                        }
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Internship'
                    }
                },
                grid: {
                    borderColor: '#f1f5f9'
                },
                legend: {
                    show: false
                }
            },
            pieChartOptions: {
                chart: {
                    type: 'donut',
                    toolbar: {
                        show: false
                    }
                },
                colors: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4'],
                labels: ['Electromechanical', 'Information Security', 'Petroleum', 'Aviation (AET/AMT)', 'Meteorology'],
                legend: {
                    position: 'bottom',
                    fontSize: '12px'
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + "%"
                    }
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total Internship',
                                    formatter: function () {
                                        return '167'
                                    }
                                }
                            }
                        }
                    }
                }
            },
            stackedChartOptions: {
                chart: {
                    type: 'bar',
                    stacked: true,
                    toolbar: {
                        show: false
                    }
                },
                colors: ['#3B82F6', '#10B981', '#8B5CF6'],
                plotOptions: {
                    bar: {
                        borderRadius: 4,
                        columnWidth: '60%',
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '10px',
                        fontWeight: 'bold'
                    }
                },
                xaxis: {
                    categories: ['Electromechanical', 'Information Security', 'Petroleum', 'Aviation (AET/AMT)', 'Meteorology'],
                    labels: {
                        style: {
                            fontSize: '11px'
                        }
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Internship'
                    }
                },
                legend: {
                    position: 'top',
                    fontSize: '12px'
                },
                grid: {
                    borderColor: '#f1f5f9'
                }
            },
            // 2023-2024 Chart Data
            periodChartOptions2023: {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    }
                },
                colors: ['#3B82F6', '#8B5CF6'],
                plotOptions: {
                    bar: {
                        columnWidth: '60%',
                        distributed: true,
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }
                },
                xaxis: {
                    categories: ['Summer 1', 'Spring'],
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Internship'
                    }
                },
                grid: {
                    borderColor: '#f1f5f9'
                }
            },
            departmentChartOptions2023: {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    }
                },
                colors: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4'],
                plotOptions: {
                    bar: {
                        columnWidth: '70%',
                        horizontal: true,
                        distributed: true,
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '11px',
                        fontWeight: 'bold'
                    }
                },
                xaxis: {
                    categories: ['Electromechanical', 'Information Security', 'Petroleum', 'Aviation (AET/AMT)', 'Meteorology'],
                    labels: {
                        style: {
                            fontSize: '11px'
                        }
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Internship'
                    }
                },
                grid: {
                    borderColor: '#f1f5f9'
                },
                legend: {
                    show: false
                }
            },
            pieChartOptions2023: {
                chart: {
                    type: 'donut',
                    toolbar: {
                        show: false
                    }
                },
                colors: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4'],
                labels: ['Electromechanical', 'Information Security', 'Petroleum', 'Aviation (AET/AMT)', 'Meteorology'],
                legend: {
                    position: 'bottom',
                    fontSize: '12px'
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + "%"
                    }
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total Internship',
                                    formatter: function () {
                                        return '110'
                                    }
                                }
                            }
                        }
                    }
                }
            },
            stackedChartOptions2023: {
                chart: {
                    type: 'bar',
                    stacked: true,
                    toolbar: {
                        show: false
                    }
                },
                colors: ['#3B82F6', '#8B5CF6'],
                plotOptions: {
                    bar: {
                        borderRadius: 4,
                        columnWidth: '60%',
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '10px',
                        fontWeight: 'bold'
                    }
                },
                xaxis: {
                    categories: ['Electromechanical', 'Information Security', 'Petroleum', 'Aviation (AET/AMT)', 'Meteorology'],
                    labels: {
                        style: {
                            fontSize: '11px'
                        }
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Internship'
                    }
                },
                legend: {
                    position: 'top',
                    fontSize: '12px'
                },
                grid: {
                    borderColor: '#f1f5f9'
                }
            },
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        user_type() {
            return this.$store.getters.user_type;
        },
        isItActiveUser() {
            if (this.user_type == 'school_active' || this.user_type == 'college_active' || this.user_type == 'school_graduate_college_active') {
                return true;
            } else {
                return false;
            }
        },
        isItGraduatingUser() {
            if (this.user_type == 'school_graduating' || this.user_type == 'college_graduating' || this.user_type == 'school_graduate_college_graduating') {
                return true;
            } else {
                return false;
            }
        },
        isActiveStudent() {
            if (this.role == 'user' && (this.isItActiveUser || this.isItGraduatingUser)) {
                return true;
            } else {
                return false;
            }
        },
    },
    apollo: {
        internshipPlacementStats: {
            query: gql`
                query GetInternshipPlacementStats {
                    internshipPlacementStats {
                        academic_year
                        summary {
                            summer1
                            summer2
                            spring
                            total
                        }
                        departments {
                            name
                            summer1
                            summer2
                            spring
                            total
                        }
                        placementBreakdown {
                            placement_type
                            count
                            percentage
                        }
                    }
                }
            `,
            fetchPolicy: 'cache-and-network',
            result({ data, loading }) {
                this.isLoading = loading;
                if (!loading && data && data.internshipPlacementStats) {
                    this.processInternshipData(data.internshipPlacementStats);
                }
            },
            error(error) {
                console.error('Error fetching internship data:', error);
                this.isLoading = false;
            }
        }
    },
    methods: {
        processInternshipData(stats) {
            // Process the data from GraphQL and update the local data
            this.academicYears = stats;
        },

        // Get data for a specific academic year
        getAcademicYearData(academicYear) {
            return this.academicYears.find(year => year.academic_year === academicYear);
        },

        // Safe getter for academic year data with fallback
        getAcademicYearSummary(academicYear, field) {
            const data = this.getAcademicYearData(academicYear);
            return data && data.summary ? data.summary[field] : 0;
        },

        // Safe getter for academic year departments
        getAcademicYearDepartments(academicYear) {
            const data = this.getAcademicYearData(academicYear);
            return data && data.departments ? data.departments : [];
        },

        // Get period chart series for any academic year
        getPeriodChartSeries(academicYear) {
            const data = this.getAcademicYearData(academicYear);
            if (!data) return [{ name: 'Interns', data: [0, 0, 0] }];

            // Check if Summer 2 exists for this academic year
            const hasSummer2 = data.summary.summer2 > 0;

            if (hasSummer2) {
                return [{
                    name: 'Interns',
                    data: [data.summary.summer1, data.summary.summer2, data.summary.spring]
                }];
            } else {
                return [{
                    name: 'Interns',
                    data: [data.summary.summer1, data.summary.spring]
                }];
            }
        },

        // Get department chart series for any academic year
        getDepartmentChartSeries(academicYear) {
            const data = this.getAcademicYearData(academicYear);
            if (!data) return [{ name: 'Total Internship', data: [] }];

            return [{
                name: 'Total Internship',
                data: data.departments.map(dept => dept.total)
            }];
        },

        // Get pie chart series for any academic year
        getPieChartSeries(academicYear) {
            const data = this.getAcademicYearData(academicYear);
            if (!data) return [];

            return data.departments.map(dept => dept.total);
        },

        // Get stacked chart series for any academic year
        getStackedChartSeries(academicYear) {
            const data = this.getAcademicYearData(academicYear);
            if (!data) return [];

            const hasSummer2 = data.summary.summer2 > 0;

            if (hasSummer2) {
                return [
                    {
                        name: 'Summer 1',
                        data: data.departments.map(dept => dept.summer1)
                    },
                    {
                        name: 'Summer 2',
                        data: data.departments.map(dept => dept.summer2)
                    },
                    {
                        name: 'Spring',
                        data: data.departments.map(dept => dept.spring)
                    }
                ];
            } else {
                return [
                    {
                        name: 'Summer 1',
                        data: data.departments.map(dept => dept.summer1)
                    },
                    {
                        name: 'Spring',
                        data: data.departments.map(dept => dept.spring)
                    }
                ];
            }
        },

        // Get pie chart options for any academic year
        getPieChartOptions(academicYear) {
            const data = this.getAcademicYearData(academicYear);
            if (!data) return this.pieChartOptions;

            return {
                ...this.pieChartOptions,
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total Internship',
                                    formatter: () => {
                                        return data.summary.total.toString();
                                    }
                                }
                            }
                        }
                    }
                }
            };
        },

        // Get top performing department for a specific academic year
        getTopPerformingDepartment(academicYear) {
            const data = this.getAcademicYearData(academicYear);
            if (!data || !data.departments || data.departments.length === 0) {
                return 'No data available';
            }

            // Find the department with the highest total internships
            const topDepartment = data.departments.reduce((max, dept) =>
                dept.total > max.total ? dept : max
            );

            // Calculate percentage of total
            const percentage = data.summary.total > 0
                ? ((topDepartment.total / data.summary.total) * 100).toFixed(1)
                : 0;

            return `${topDepartment.name} leads with ${topDepartment.total} internship (${percentage}% of total)`;
        },

        // Get peak period for a specific academic year
        getPeakPeriod(academicYear) {
            const data = this.getAcademicYearData(academicYear);
            if (!data || !data.summary) {
                return 'No data available';
            }

            const periods = [
                { name: 'Summer 1', value: data.summary.summer1 },
                { name: 'Summer 2', value: data.summary.summer2 },
                { name: 'Spring', value: data.summary.spring }
            ];

            // Filter out periods with 0 values and find the peak
            const activePeriods = periods.filter(period => period.value > 0);

            if (activePeriods.length === 0) {
                return 'No internship data available';
            }

            const peakPeriod = activePeriods.reduce((max, period) =>
                period.value > max.value ? period : max
            );

            return `${peakPeriod.name} semester shows highest activity with ${peakPeriod.value} internship`;
        },


        // Get placement breakdown for a specific academic year
        getPlacementBreakdown(academicYear) {
            const data = this.getAcademicYearData(academicYear);
            return data && data.placementBreakdown ? data.placementBreakdown : [];
        },

        // Get placement type chart series
        getPlacementTypeChartSeries(academicYear) {
            const breakdown = this.getPlacementBreakdown(academicYear);
            return breakdown.map(item => item.count);
        },

        // Get placement type chart options
        getPlacementTypeChartOptions(academicYear) {
            const breakdown = this.getPlacementBreakdown(academicYear);
            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#EF4444', '#84CC16', '#F97316'];

            return {
                chart: {
                    type: 'donut',
                    toolbar: {
                        show: false
                    }
                },
                colors: colors.slice(0, breakdown.length),
                labels: breakdown.map(item => item.placement_type),
                legend: {
                    position: 'bottom',
                    fontSize: '12px'
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + "%"
                    }
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total Placements',
                                    formatter: function () {
                                        return breakdown.reduce((sum, item) => sum + item.count, 0).toString();
                                    }
                                }
                            }
                        }
                    }
                }
            };
        },

        // Get color for placement type (matches chart colors)
        getPlacementColor(placementType, academicYear) {
            const breakdown = this.getPlacementBreakdown(academicYear);
            if (!breakdown || breakdown.length === 0) return '#6B7280';

            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#EF4444', '#84CC16', '#F97316'];
            const index = breakdown.findIndex(item => item.placement_type === placementType);

            return index >= 0 ? colors[index] : '#6B7280';
        },


        // Get most popular placement type
        getMostPopularPlacement(academicYear) {
            const breakdown = this.getPlacementBreakdown(academicYear);
            if (!breakdown || breakdown.length === 0) return 'No data available';

            const mostPopular = breakdown[0]; // Already sorted by count descending
            return `${mostPopular.placement_type} leads with ${mostPopular.count} placements (${mostPopular.percentage}%)`;
        }
    }
}
</script>

<style scoped>
/* Shimmer Animation */
.shimmer {
    position: relative;
    overflow: hidden;
}

.shimmer::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
            transparent,
            rgba(255, 255, 255, 0.4),
            transparent);
    animation: shimmer 2s infinite;
}

.shimmer-bar {
    position: relative;
    overflow: hidden;
}

.shimmer-bar::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
            transparent,
            rgba(255, 255, 255, 0.6),
            transparent);
    animation: shimmer 2s infinite;
}

@keyframes shimmer {
    0% {
        left: -100%;
    }

    100% {
        left: 100%;
    }
}

/* Smooth transitions */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
